import React, { useMemo, useState } from 'react';
import {
  Button, Checkbox, Divider, Modal, Tag,
} from 'antd';
import {
  CheckCircleFilled,
  CloseCircleFilled,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { WL_CONFIG } from 'constants';
import { StyledWrapper, StyledModalContent } from './styles';

const SUPPORT_EMAIL = WL_CONFIG.supportEmail;

const MANUAL_CHECKS = [
  'Address on Google Business Profile matches exactly, including any suite / unit number',
  'Business name matches the Google Business Profile name',
  'Phone number matches',
  'Google Business Profile is verified & published (not pending or permanently closed)',
  'Business hours are set on Google',
];

function EligibilityRow({ label, hint, passed }) {
  return (
    <div className="check-row">
      {passed ? (
        <CheckCircleFilled className="status-icon status-icon--pass" />
      ) : (
        <CloseCircleFilled className="status-icon status-icon--fail" />
      )}
      <div className="check-label">
        <span>{label}</span>
        {!passed && hint ? <span className="check-hint">{hint}</span> : null}
      </div>
    </div>
  );
}

function ValueRow({ label, value }) {
  return (
    <div className="value-row">
      <span className="value-label">{label}</span>
      <span className="value-data">{value || '—'}</span>
    </div>
  );
}

function GfoVerificationBox({ merchantData }) {
  const [open, setOpen] = useState(false);
  const [manualChecked, setManualChecked] = useState([]);

  const eligibility = merchantData?.gfo_eligibility;
  const checks = useMemo(() => eligibility?.checks || [], [eligibility]);
  const allEligible = Boolean(eligibility?.eligible);
  const allManualChecked = manualChecked.length === MANUAL_CHECKS.length;

  const supportSubject = encodeURIComponent(
    `GFO listing not appearing – ${merchantData?.name || ''} (ID: ${merchantData?.id || ''})`,
  );

  const summaryNote = () => {
    if (!allEligible) {
      return 'One or more Nibble eligibility checks are failing. Fix those first \u2014 the restaurant cannot appear on Google until they pass.';
    }
    if (!allManualChecked) {
      return 'All Nibble eligibility checks pass. Confirm each Google Business Profile item above with the merchant before escalating.';
    }
    return 'Everything checks out. Google can take a few days to sync a new match \u2014 if the listing still doesn\u2019t appear after that, contact support.';
  };

  return (
    <StyledWrapper>
      <div className="box">
        <div className="box-header">
          <div className="box-title">
            Google Food Ordering
            <Tag color={allEligible ? 'success' : 'warning'}>
              {allEligible ? 'Eligible' : 'Not eligible'}
            </Tag>
          </div>
          <Button type="primary" size="middle" onClick={() => setOpen(true)}>
            Verify listing
          </Button>
        </div>
        <div className="box-desc">
          <InfoCircleOutlined className="info-icon" />
          <span>
            Not showing on Google Search / Maps? Run through the verification
            checklist before contacting support.
          </span>
        </div>
      </div>

      <Modal
        title="Why isn't this restaurant on Google Food Ordering?"
        open={open}
        onCancel={() => setOpen(false)}
        footer={[
          <Button key="close" onClick={() => setOpen(false)}>
            Close
          </Button>,
          SUPPORT_EMAIL ? (
            <Button
              key="support"
              type="primary"
              href={`mailto:${SUPPORT_EMAIL}?subject=${supportSubject}`}
            >
              Contact support
            </Button>
          ) : null,
        ].filter(Boolean)}
        width={560}
      >
        <StyledModalContent>
          <div className="section-title">1. Nibble eligibility</div>
          <div className="section-subtitle">
            These are required for a restaurant to be included in the Google feed.
            A red mark means it will not reach Google until fixed on our side.
          </div>
          <div className="check-list">
            {checks.length ? (
              checks.map((item) => (
                <EligibilityRow
                  key={item.key}
                  label={item.label}
                  hint={item.hint}
                  passed={item.passed}
                />
              ))
            ) : (
              <span className="check-empty">Eligibility data is unavailable.</span>
            )}
          </div>

          <Divider />

          <div className="section-title">2. Google Business Profile match</div>
          <div className="section-subtitle">
            Google matches our feed to the merchant&apos;s Business Profile. Ask
            the merchant to confirm each item below matches exactly what we have
            on file.
          </div>

          <div className="current-values">
            <ValueRow label="Business name" value={merchantData?.name} />
            <ValueRow label="Address" value={merchantData?.address} />
            <ValueRow label="Phone" value={merchantData?.phone} />
            <ValueRow label="Website" value={merchantData?.website} />
          </div>

          <Checkbox.Group
            className="manual-list"
            value={manualChecked}
            onChange={setManualChecked}
            options={MANUAL_CHECKS.map((label, index) => ({ label, value: index }))}
          />

          <Divider />

          <div className="support-note">{summaryNote()}</div>
        </StyledModalContent>
      </Modal>
    </StyledWrapper>
  );
}

export default GfoVerificationBox;
